// Revenue triage — segmented status tabs shared by the Income and Expenses
// tabs (QBO-style For review / Categorized / Excluded / All). Dumb component:
// the owning tab keeps all URL-param state and passes counts down.

import { isBankFeedAwaitingReview } from "@shared/bank-feed-review";
export type ReviewStatusFilter = "for_review" | "categorized" | "excluded" | "all";

export const REVIEW_STATUS_LABELS: Record<ReviewStatusFilter, string> = {
  for_review: "For review",
  categorized: "Categorized",
  excluded: "Excluded",
  all: "All",
};

const STATUS_ORDER: ReviewStatusFilter[] = ["for_review", "categorized", "excluded", "all"];

/**
 * Derive a row's triage bucket. Voided/excluded rows never sit in the queue;
 * everything else splits on the human-confirmation stamp.
 */
export function deriveReviewStatus(row: {
  reviewedAt?: Date | string | null;
  excludedAt?: Date | string | null;
  voidedAt?: Date | string | null;
  externalSource?: string | null;
}): Exclude<ReviewStatusFilter, "all"> {
  if (isBankFeedAwaitingReview(row)) return "for_review";
  if (row.excludedAt || row.voidedAt) return "excluded";
  return row.reviewedAt ? "categorized" : "for_review";
}

export function StatusTabs({
  value,
  onChange,
  counts,
  idPrefix,
}: {
  value: ReviewStatusFilter;
  onChange: (next: ReviewStatusFilter) => void;
  /** Row count per bucket (already filtered by search/property/date). */
  counts: Record<ReviewStatusFilter, number>;
  /** Distinguishes the two tabs' testids, e.g. "expenses" / "income". */
  idPrefix: string;
}) {
  return (
    <div
      className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5"
      role="tablist"
      data-testid={`status-tabs-${idPrefix}`}
    >
      {STATUS_ORDER.map((key) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${
              active
                ? "bg-white text-gray-900 font-medium shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
            data-testid={`tab-${idPrefix}-status-${key}`}
          >
            {REVIEW_STATUS_LABELS[key]}
            {key === "for_review" && counts.for_review > 0 && (
              <span
                className={`ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                  active ? "bg-amber-100 text-amber-800" : "bg-gray-200 text-gray-600"
                }`}
                data-testid={`count-${idPrefix}-for-review`}
              >
                {counts.for_review}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
